import React from 'react'
import intl from 'react-intl-universal'
import { LOCALE } from '@foxrenderfarm/internals'
import {
  ArnoldSolutionIcon,
  BlenderSolutionIcon,
  C4DSolutionIcon,
  HoudiniSolutionIcon,
  MayaSolutionIcon,
  MaxSolutionIcon,
  NukeSolutionIcon,
  SupportCenterIcon,
  UnrealSolutionIcon,
  VRaySolutionIcon,
} from '../../../svg'
import { showContact } from '../../../../../utils'
import { MainLink, getLocalAppOrigin, getSupportSectionTitle } from '../../light-dropdowns/common'
import { MobileNavAccordion } from '../mobile-nav-accordion'
import { MobileNavIconGrid, MobileNavSupportBanner } from '../atoms'
import { LightDropDownRow } from '../../../atoms'

interface SupportPanelProps {
  currentLocale: LOCALE
  origin: string
}

export const SupportPanel = ({ currentLocale, origin }: SupportPanelProps) => {
  const useSupport = process.env.GATSBY_PACKAGE_NAME === 'support'
  const supportOrigin = getLocalAppOrigin(origin, 8087)

  const tutorialLinks = [
    { to: '/support/rendering-tutorial/blender/', label: 'Blender', Icon: BlenderSolutionIcon },
    { to: '/support/rendering-tutorial/3ds-max/', label: '3ds Max', Icon: MaxSolutionIcon },
    { to: '/support/rendering-tutorial/maya/', label: 'Maya', Icon: MayaSolutionIcon },

    { to: '/support/rendering-tutorial/cinema-4-d/', label: 'Cinema 4D', Icon: C4DSolutionIcon },
    {
      to: '/support/rendering-tutorial/fox-support-dir-4/',
      label: 'Houdini',
      Icon: HoudiniSolutionIcon,
    },
    {
      to: '/support/rendering-tutorial/unreal-engine/',
      label: 'Unreal Engine',
      Icon: UnrealSolutionIcon,
    },
    {
      to: '/support/rendering-tutorial/arnold-standalone/',
      label: 'Arnold',
      Icon: ArnoldSolutionIcon,
    },
    { to: '/support/rendering-tutorial/vray/', label: 'V-Ray', Icon: VRaySolutionIcon },
    { to: '/support/rendering-tutorial/fox-support-dir-3/', label: 'Nuke', Icon: NukeSolutionIcon },
  ]

  return (
    <>
      <MobileNavAccordion title={getSupportSectionTitle(currentLocale, 'guides')} nested>
        <MainLink
          to='/support/general/'
          href={`${supportOrigin}/support/general/`}
          useGatsbyLink={useSupport}
          locale={currentLocale}
        >
          {intl.get('index.header.support.general')}
        </MainLink>
        <MainLink
          to='/support/developer-center/'
          href={`${supportOrigin}/support/developer-center/`}
          useGatsbyLink={useSupport}
          locale={currentLocale}
        >
          {intl.get('index.header.support.developerCenter')}
        </MainLink>
      </MobileNavAccordion>

      <MobileNavAccordion title={getSupportSectionTitle(currentLocale, 'account')} nested>
        <MainLink
          to='/support/user-center/'
          href={`${supportOrigin}/support/user-center/`}
          useGatsbyLink={useSupport}
          locale={currentLocale}
        >
          {intl.get('index.header.support.user')}
        </MainLink>
        <MainLink
          to='/support/pricing-and-billing/'
          href={`${supportOrigin}/support/pricing-and-billing/`}
          useGatsbyLink={useSupport}
          locale={currentLocale}
        >
          {intl.get('index.header.support.pricing.billing')}
        </MainLink>
        <LightDropDownRow as='button' type='button' onClick={showContact}>
          {intl.get('index.header.support.contact247')}
        </LightDropDownRow>
      </MobileNavAccordion>

      <MobileNavAccordion title={getSupportSectionTitle(currentLocale, 'renderingTutorial')} nested>
        <MobileNavIconGrid>
          {tutorialLinks.map((item) => {
            const Icon = item.Icon
            return (
              <MainLink
                key={item.to}
                to={item.to}
                href={`${supportOrigin}${item.to}`}
                useGatsbyLink={useSupport}
                locale={currentLocale}
              >
                {Icon && <Icon />}
                {item.label}
              </MainLink>
            )
          })}
        </MobileNavIconGrid>
      </MobileNavAccordion>

      <MobileNavSupportBanner href={`${supportOrigin}/support/`}>
        {intl.get('index.header.support.center')}
        <SupportCenterIcon />
      </MobileNavSupportBanner>
    </>
  )
}
